'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { jsPDF } from 'jspdf';
import {
  canvasToBlob,
  createCleanorExportFileName,
  createPdfPreviewUrl,
  formatFileSize,
  getRenderScale,
  isPdfFile,
  openPdfDocument,
  renderPdfPageToCanvas,
  type PdfJsDocument,
} from '@/lib/browser-pdf-tools';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';

type ProtectSource = {
  id: string;
  file: File;
  name: string;
  size: number;
  pageCount: number;
  previewUrl: string | null;
};

type ProtectProgress = {
  current: number;
  total: number;
  label: string;
};

type PermissionPreset = 'view-only' | 'print' | 'copy-print';

const permissionPresets: Array<{
  id: PermissionPreset;
  label: string;
  permissions: Array<'print' | 'copy'>;
  hint: string;
}> = [
  {
    id: 'view-only',
    label: 'View only',
    permissions: [],
    hint: 'Password required to open. Printing and copying stay locked down.',
  },
  {
    id: 'print',
    label: 'Allow printing',
    permissions: ['print'],
    hint: 'Password required to open, but printing remains available.',
  },
  {
    id: 'copy-print',
    label: 'Allow copy and print',
    permissions: ['print', 'copy'],
    hint: 'Password required to open, while print and copy stay available.',
  },
] as const;

async function readSource(file: File): Promise<ProtectSource> {
  const document = await openPdfDocument(new Uint8Array(await file.arrayBuffer()));

  try {
    return {
      id: crypto.randomUUID(),
      file,
      name: file.name,
      size: file.size,
      pageCount: document.numPages,
      previewUrl: await createPdfPreviewUrl(document),
    };
  } catch {
    throw new Error(`"${file.name}" could not be opened. Use a standard unlocked PDF file.`);
  } finally {
    document.cleanup?.();
    await document.destroy?.();
  }
}

function createExportFileName(pageCount: number) {
  const pageLabel = pageCount === 1 ? '1-page' : `${pageCount}-pages`;
  return createCleanorExportFileName('protect-pdf', [pageLabel], 'pdf');
}

function triggerDownloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export default function BrowserProtectPdf() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [source, setSource] = useState<ProtectSource | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [permissionPreset, setPermissionPreset] = useState<PermissionPreset>('view-only');
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProtecting, setIsProtecting] = useState(false);
  const [progress, setProgress] = useState<ProtectProgress | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);

  const activePreset =
    permissionPresets.find((preset) => preset.id === permissionPreset) ?? permissionPresets[0];

  useEffect(() => {
    return () => {
      if (source?.previewUrl) {
        URL.revokeObjectURL(source.previewUrl);
      }
    };
  }, [source]);

  function replaceSource(nextSource: ProtectSource | null) {
    setSource((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      return nextSource;
    });
  }

  async function handleFileSelection(file: File | null) {
    setErrorMessage(null);
    setCreditMessage(null);
    setResultSize(null);

    if (!file) {
      replaceSource(null);
      return;
    }

    if (!isPdfFile(file)) {
      replaceSource(null);
      setErrorMessage('Choose one PDF file to protect.');
      return;
    }

    try {
      replaceSource(await readSource(file));
    } catch (error) {
      replaceSource(null);
      setErrorMessage(error instanceof Error ? error.message : 'This PDF could not be opened.');
    }
  }

  function clearSource() {
    replaceSource(null);
    setErrorMessage(null);
    setCreditMessage(null);
    setResultSize(null);
    setProgress(null);

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  async function protectPdf() {
    if (!source) {
      setErrorMessage('Choose a PDF first.');
      return;
    }

    if (!password.trim()) {
      setErrorMessage('Enter a password first.');
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    setIsProtecting(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setProgress({ current: 0, total: source.pageCount, label: 'Opening PDF…' });

    let document: PdfJsDocument | null = null;

    try {
      const credits = await consumeToolUsage(
        'protect-pdf',
        'Credits are not available for another protected PDF export right now.',
      );

      document = await openPdfDocument(new Uint8Array(await source.file.arrayBuffer()));
      const firstPage = await document.getPage(1);
      const firstViewport = firstPage.getViewport({ scale: 1 });
      firstPage.cleanup?.();

      const exportDoc = new jsPDF({
        orientation: firstViewport.width > firstViewport.height ? 'l' : 'p',
        unit: 'pt',
        format: [firstViewport.width, firstViewport.height],
        compress: true,
        encryption: {
          userPassword: password,
          ownerPassword: crypto.randomUUID(),
          userPermissions: activePreset.permissions,
        },
      });

      for (let pageIndex = 0; pageIndex < source.pageCount; pageIndex += 1) {
        const pageNumber = pageIndex + 1;
        setProgress({
          current: pageNumber,
          total: source.pageCount,
          label: `Protecting page ${pageNumber} of ${source.pageCount}…`,
        });

        const page = await document.getPage(pageNumber);

        try {
          const baseViewport = page.getViewport({ scale: 1 });
          const renderScale = getRenderScale(baseViewport, 1.9);
          const { canvas, viewport } = await renderPdfPageToCanvas(page, renderScale);
          const blob = await canvasToBlob(canvas, 'image/jpeg', 0.78);
          const dataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result));
            reader.onerror = () => reject(new Error('This page could not be encoded for export.'));
            reader.readAsDataURL(blob);
          });

          if (pageIndex > 0) {
            exportDoc.addPage(
              [viewport.width, viewport.height],
              viewport.width > viewport.height ? 'landscape' : 'portrait',
            );
          }

          exportDoc.setPage(pageIndex + 1);
          exportDoc.addImage(
            dataUrl,
            'JPEG',
            0,
            0,
            viewport.width,
            viewport.height,
            undefined,
            'FAST',
          );
          canvas.width = 0;
          canvas.height = 0;
        } finally {
          page.cleanup?.();
        }
      }

      const blob = exportDoc.output('blob');
      setResultSize(blob.size);
      triggerDownloadBlob(blob, createExportFileName(source.pageCount));
      setCreditMessage(formatToolCreditSuccess('Protected PDF export', credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'This PDF could not be protected in the browser.',
      );
    } finally {
      setProgress(null);
      setIsProtecting(false);
      document?.cleanup?.();
      await document?.destroy?.();
    }
  }

  return (
    <article className="tool-live-card tool-pdf-card">
      <div className="tool-live-header">
        <div>
          <h2>Protect one PDF with a password</h2>
          <p>
            Add an opening password to a PDF in the browser and export the protected version right
            away without sending the file to a server.
          </p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>
                {source ? 'PDF ready for protection' : 'Choose one PDF or drop it here'}
              </strong>
              <span>
                {source
                  ? `${source.pageCount} pages • ${formatFileSize(source.size)}`
                  : 'This browser-first version rebuilds pages locally before encrypting the export'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="application/pdf,.pdf"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls tool-pdf-settings">
            <label className="tool-live-field">
              <span>Password</span>
              <input
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Enter a password"
                type="password"
                value={password}
              />
            </label>
            <label className="tool-live-field">
              <span>Confirm password</span>
              <input
                onChange={(event) => setConfirmPassword(event.target.value)}
                placeholder="Repeat the password"
                type="password"
                value={confirmPassword}
              />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Permissions</span>
              <select
                onChange={(event) => setPermissionPreset(event.target.value as PermissionPreset)}
                value={permissionPreset}
              >
                {permissionPresets.map((preset) => (
                  <option key={preset.id} value={preset.id}>
                    {preset.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isProtecting}
              onClick={() => void protectPdf()}
              type="button"
            >
              {isProtecting ? 'Protecting PDF…' : 'Protect PDF'}
            </button>
            {source ? (
              <button className="button button-ghost" onClick={clearSource} type="button">
                Clear file
              </button>
            ) : null}
          </div>

          {progress ? (
            <div className="tool-pdf-progress">
              <span>{progress.label}</span>
              <progress max={progress.total} value={progress.current} />
            </div>
          ) : null}
          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Local protected export</h3>
          <div className="tool-mockup-chip-list">
            <span>PDF → PDF</span>
            <span>Password</span>
            <span>{activePreset.label}</span>
            <span>Local export</span>
          </div>
          <p>{activePreset.hint}</p>
          <div className="tool-live-panel-note">
            <strong>Important tradeoff</strong>
            <span>
              To keep protection browser-first, this tool rebuilds the PDF pages as images before
              encryption. Searchable text, forms, and annotations can be flattened in the result.
            </span>
          </div>
          {source ? (
            <div className="tool-live-stats">
              <div>
                <strong>Pages</strong>
                <span>{source.pageCount}</span>
              </div>
              <div>
                <strong>Source size</strong>
                <span>{formatFileSize(source.size)}</span>
              </div>
              <div>
                <strong>Permissions</strong>
                <span>{activePreset.label}</span>
              </div>
              {resultSize ? (
                <div>
                  <strong>Last export</strong>
                  <span>{formatFileSize(resultSize)}</span>
                </div>
              ) : null}
              <div>
                <strong>Privacy</strong>
                <span>Processed in your browser</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      {source?.previewUrl ? (
        <div className="tool-live-preview">
          <h3>Source preview</h3>
          <div className="tool-live-preview-frame">
            <img alt="" src={source.previewUrl} />
          </div>
        </div>
      ) : null}
    </article>
  );
}
